import { Grid, makeStyles, Paper } from '@material-ui/core';
import { Button, ExtendedTheme, IconButton, LargeCheckCircle, LargeWarningCircle, TextBlock, Upload, UserInterfaceBin } from 'neon-design-system';
import React, { ChangeEvent, DragEvent, MouseEvent, useEffect, useRef, useState } from 'react';

const useStyles = makeStyles((theme: ExtendedTheme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(3),
		// position: 'relative',
		backgroundColor: theme.themeColors.grey[800],
		border: `2px dashed ${theme.themeColors.grey[600]}`,
		borderRadius: 4,
		textAlign: 'center',
		transition: 'border-color .3s ease-in-out',
		'&.drag-hover': {
			borderColor: theme.themeColors.grey[300]
		}
	},
	error: {
		borderColor: theme.palette.error.main
	},
	filePreview: {
		backgroundColor: theme.themeColors.grey[700],
		padding: theme.spacing(2),
		display: 'flex',
		gap: theme.spacing(2),
		alignItems: 'center',
		textAlign: 'left',
		'& > div': {
			flex: 1,
			overflow: 'hidden',
			'&::after': {
				content: '""',
				display: 'block',
				height: 3,
				marginTop: theme.spacing(1),
				backgroundColor: theme.themeColors.secondary.main
			},
			'& p': {
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis'
			}
		}
	}
}));

interface LocalFileProps {
	title?: string;
	description?: React.ReactNode;
	accept?: string;
	onFileChanged: (file?: File) => void;
	error?: boolean;
}

export const LocalFile = ({title = 'Drag file or click here to upload', description, accept, onFileChanged, error}: LocalFileProps) => {
	const fileRef = useRef<HTMLInputElement>(null);
	const classes = useStyles();
	const [draghover, setDragHover] = useState(false);
	const [formatError, setFormatError] = useState(false);
	const [success, setSuccess] = useState(false);
	const [file, setFile] = useState<File>();

	useEffect(() => {
		onFileChanged(file);
	}, [file]);

	const choose = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		fileRef.current?.click();
	}

	const addFile = (e: ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		e.preventDefault();
		const file = fileRef.current?.files?.[0]
		if (file) {
			proceedFile(file);
		}
	}

	const drop = (e: DragEvent<HTMLElement>) => {
		setDragHover(false);
		e.stopPropagation();
		e.preventDefault();
		const file = e.dataTransfer?.files?.[0]
		if (file) {
			proceedFile(file);
		}
	}

	const proceedFile = (file: File) => {
		const found = !accept ? true : accept.split(',').find(f => file.name.endsWith(f) || file.type === f);
		if (found) {
			setFormatError(false);
			setSuccess(true);
			setFile(file);
		} else {
			setSuccess(false);
			setFormatError(true);
			setFile(undefined);
		}
	}

	const hover = (hover: boolean) => (e: MouseEvent<HTMLElement>) => {
		setDragHover(hover);
		e.stopPropagation();
		e.preventDefault();
	}

	const deleteFile = () => {
		if (fileRef && fileRef.current && fileRef.current.value) fileRef.current.value = '';
		setFile(undefined);
		setSuccess(false);
		setFormatError(false);
	}

	const message = success ? 'Upload complete' : formatError ? 'File format not supported' : title;

	const attrs = !success ? {
		onClick: choose,
		onDropCapture: drop,
		onDragOverCapture: hover(true),
		onDragLeaveCapture: hover(false)
	} : {};

	return <>
		<input type="file" accept={accept} ref={fileRef} onChange={addFile} style={{display: 'none'}}/>
		<div
			{...attrs}
			className={[classes.root, draghover && 'drag-hover', error && classes.error].join(' ')}
		>
			<Grid container spacing={2} style={{pointerEvents: 'none'}}>
				<Grid item xs={12}>
					{success ? <LargeCheckCircle color="success" /> : formatError ? <LargeWarningCircle color="error" /> : <Upload /> }
				</Grid>
				<Grid item xs={12}>
					<Grid container spacing={1}>
						<Grid item xs={12}><TextBlock styleName="body"><strong>{message}</strong></TextBlock></Grid>
						{(description && !success) && <Grid item xs={12}><TextBlock styleName="caption1" ink="muted">{description}</TextBlock></Grid>}
					</Grid>
				</Grid>
				<Grid item xs={12} style={{pointerEvents: 'all'}}>
					{file ? (
						<Paper elevation={0} className={classes.filePreview}>
							<div title={file.name}>
								<TextBlock styleName="footnote"><strong>{file.name}</strong></TextBlock>
							</div>
							<IconButton width={16} onClick={deleteFile}><UserInterfaceBin /></IconButton>
						</Paper>
					) : <Button size="small" onClick={() => {}} colorType="grey">Choose file</Button>}
				</Grid>
			</Grid>
		</div>
	</>;
}
